import { z } from 'zod';
/* ==========================================================================
 *  AI provider contract (ISSUE-015, D-15, D-16, D-18)
 *
 *  Callers think in STEPS ("extraction", "chat"), never in providers. A routing
 *  table maps each step → (provider, model) and is hot-swappable via env, so the
 *  cost/quality trade-off (D-15) and the €0.30/AU/month ceiling (D-16) are tuned
 *  in config, not code.
 *
 *  CONTRACT TYPES only — no provider SDKs in @nutians/shared. The Anthropic /
 *  OpenAI clients live in the server-only `@nutians/ai` package.
 * ========================================================================== */
/** Pipeline step. Drives routing + per-step cost accounting. Aligns with the
 *  arch-doc table: extraction/validation = small models; summary/chat = large. */
export const aiStepSchema = z.enum([
    'extraction', // FR — high-volume entity extraction (small model)
    'validation', // sanity checks (small model)
    'summary', // weekly/monthly synthesis (large model)
    'summary-day', // daily synthesis (small model — cost target ISSUE-031)
    'chat', // assistant (large model)
    'recommendation', // generic macro suggestions (large model)
    'classify', // ADR-0013 Stage 0 intent pre-classifier (small model)
    'extraction-vision', // ADR-0014/0015 — Pixtral vision path (photo meal capture)
    'estimation', // AI macro estimate for DB-unresolved foods (small model; free=ai_haiku, premium=ai_sonnet)
]);
/** Providers behind the `AiClient`. `ollama` is a local, OpenAI-compatible
 *  runtime (http://localhost:11434) for running models like qwen2.5/llama3.1/
 *  phi3 offline at €0 real cost — see ADR-0007.
 *  `mistral` is the primary EU-hosted provider (ADR-0015). */
export const aiProviderSchema = z.enum(['anthropic', 'openai', 'ollama', 'mistral']);
/* ----- Messages ---------------------------------------------------------- */
export const aiRoleSchema = z.enum(['system', 'user', 'assistant', 'tool']);
/* ----- Multimodal content (ADR-0014 §3 — LOCKED) -------------------------- */
/** A text segment of a multimodal message. */
export const aiTextPartSchema = z.object({
    type: z.literal('text'),
    text: z.string(),
});
/** An image segment of a multimodal message (ADR-0014 §3.1). `data` is either a
 *  base64-encoded image payload (no `data:` URI prefix — the adapter adds it) or
 *  a short-lived https URL. `mediaType` is the MIME type the provider needs to
 *  decode it (e.g. `image/jpeg`, `image/png`, `image/webp`).
 *
 *  This is the *provider-agnostic* shape; each adapter maps it to its wire form
 *  (OpenAI/Mistral `image_url`, Anthropic `image` source blocks). */
export const aiImagePartSchema = z.object({
    type: z.literal('image'),
    mediaType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'image/gif']),
    data: z.string().min(1),
});
/** A single part of a multimodal message: text or image. */
export const aiContentPartSchema = z.discriminatedUnion('type', [
    aiTextPartSchema,
    aiImagePartSchema,
]);
/** A message to the AI. `content` is either a plain string (the original
 *  text-only form — fully back-compat) OR an array of multimodal parts
 *  (ADR-0014: text + image, for the Pixtral `extraction-vision` path). Only the
 *  `user` role carries image parts in practice; system/assistant/tool messages
 *  stay text. `toolCallId` is set only on `tool` result messages. */
export const aiMessageSchema = z.object({
    role: aiRoleSchema,
    content: z.union([z.string(), z.array(aiContentPartSchema)]),
    toolCallId: z.string().optional(),
});
/** Flatten a (possibly multimodal) message content to its plain text, dropping
 *  image parts. Used by adapters/log paths that need a string and by non-vision
 *  providers. Returns the string as-is when content is already a string. */
export function aiContentToText(content) {
    if (typeof content === 'string')
        return content;
    return content
        .filter((part) => part.type === 'text')
        .map((part) => part.text)
        .join('\n');
}
/** True when any part of the message content is an image (ADR-0014 vision). */
export function aiMessageHasImage(content) {
    return typeof content !== 'string' && content.some((part) => part.type === 'image');
}
/* ----- Usage + cost (ISSUE-015 → ai_usage table → ISSUE-207 KPI) ---------- */
/** Per-call usage. Persisted to `ai_usage`; aggregated per-user-per-month for
 *  the AI-cost-per-AU KPI (ISSUE-207) and the D-16 ceiling check. */
export const aiUsageSchema = z.object({
    provider: aiProviderSchema,
    model: z.string(),
    step: aiStepSchema,
    /** Caller's entitlement tier at the time of the call (ADR-0012 §4 / M-D1).
     *  Stored on `ai_usage.tier` so the admin portal can split free vs premium
     *  cost/AU. Defaults to 'premium' for system/cron calls with no user context. */
    tier: z.enum(['free', 'premium']).default('premium').optional(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    costEur: z.number().nonnegative(),
    /** ADR-0007 §4 Resolution B1: set when the route uses @priceAs and it differs
     *  from the physical model (e.g. `ollama:phi3@gpt-4o-mini` → pricedAsModel =
     *  'gpt-4o-mini'). Null for real cloud calls and bare Ollama routes. Lets the
     *  admin portal split actual vs projected spend. */
    pricedAsModel: z.string().nullable().optional(),
});
/** Full row shape for the `ai_usage` table (usage + attribution + time). */
export const aiUsageRecordSchema = aiUsageSchema.extend({
    userId: z.string().nullable(), // null for system/cron calls (e.g. weekly job)
    promptId: z.string().nullable().default(null),
    promptVersion: z.string().nullable().default(null),
    createdAt: z.string().datetime(),
});
/** Per-model price table entry (€ per 1M tokens). Lets cost be computed
 *  deterministically and unit-tested, like the TDEE math. */
export const modelPriceSchema = z.object({
    inputPerMTokensEur: z.number().nonnegative(),
    outputPerMTokensEur: z.number().nonnegative(),
});
/** Pure cost helper — kept here (not in an adapter) so it is provider-agnostic
 *  and trivially testable. */
export function computeCostEur(inputTokens, outputTokens, price) {
    const cost = (inputTokens / 1_000_000) * price.inputPerMTokensEur +
        (outputTokens / 1_000_000) * price.outputPerMTokensEur;
    // Round to 6 dp (sub-cent precision) to keep aggregation honest.
    return Math.round(cost * 1_000_000) / 1_000_000;
}
/* ----- Routing ----------------------------------------------------------- */
/** One step's routing: primary provider/model + optional failover target
 *  (used on persistent 429/5xx — see RetryPolicy).
 *
 *  `priceAs` is the COST-SIMULATION knob (ADR-0007): when set, usage is costed
 *  at this model's price table entry instead of the model actually run. Lets you
 *  run a local Ollama model (real cost €0) while projecting what a comparable
 *  cloud model would have cost — e.g. `ollama:phi3` priced as `gpt-4o-mini`.
 *  Env form: `AI_ROUTE_CHAT=ollama:phi3@gpt-4o-mini`. */
export const stepRouteSchema = z.object({
    provider: aiProviderSchema,
    model: z.string(),
    fallback: z.object({ provider: aiProviderSchema, model: z.string() }).nullable().default(null),
    priceAs: z.string().nullable().optional(),
});
/** Flat routing table (ADR-0007 original shape). Kept for backward-compat; the
 *  live config is now `AiTieredRoutingConfig`. */
export const aiRoutingConfigSchema = z.record(aiStepSchema, stepRouteSchema);
// ── Tiered routing (ADR-0012 §4) ──────────────────────────────────────────────
/** User plan tier — resolved from the entitlement seam and threaded through
 *  every AI call so the client can pick the correct model variant. */
export const aiTierSchema = z.enum(['free', 'premium']);
/** Per-step tiered routes: premium gets the full model; free gets a cheaper
 *  variant, or null when the step is premium-only (caller must gate before
 *  calling AI — M-B2/M-C2 will enforce these gates). */
export const stepTieredRouteSchema = z.object({
    premium: stepRouteSchema,
    free: stepRouteSchema.nullable(),
});
/** Entitlement-aware routing table. Replaces the flat `AiRoutingConfig` as the
 *  live config shape; `AiRoutingConfig` is kept for backward-compat only. */
export const aiTieredRoutingConfigSchema = z.record(aiStepSchema, stepTieredRouteSchema);
export const retryPolicySchema = z.object({
    maxAttempts: z.number().int().min(1).default(3),
    baseDelayMs: z.number().int().positive().default(500),
    /** After this many failed attempts on the primary, switch to `fallback`. */
    failoverAfterAttempts: z.number().int().min(1).default(2),
});
/* ----- Prompts (ISSUE-102 owns content; this is just the reference shape) - */
/** A versioned prompt reference. Prompt bodies + changelog live in
 *  `packages/shared/prompts/` (ISSUE-102). Carrying the ref on each call lets
 *  usage rows attribute cost to a prompt version for eval/regression
 *  (ISSUE-103). */
export const promptRefSchema = z.object({
    id: z.string(), // e.g. "extraction.meal"
    version: z.string(), // e.g. "2026-05-30" or semver
});
//# sourceMappingURL=ai.js.map